import { format } from 'date-fns';
import { useState } from 'react';
import { Box, HStack, Tabs, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import type {
  CalendarAttendeeResponse,
  CalendarEvent,
  CalendarEventAttendee,
  CalendarLeadInviteSuppressedReason,
} from '../../../types/calendar';
import { EventSourceIndicator } from '../indicators/EventSourceIndicator';
import { EventAttendeeStack } from './EventAttendeeStack';
import { JoinCallButton } from './JoinCallButton';
import { OpenLeadLink } from './OpenLeadLink';

// An RSVP may never ride on colour: each state carries its word.
const RESPONSE_LABELS: Record<CalendarAttendeeResponse, string> = {
  accepted: 'Accepted',
  declined: 'Declined',
  tentative: 'Maybe',
  needs_action: 'Pending',
};

const RESPONSE_COLORS: Record<CalendarAttendeeResponse, string> = {
  accepted: colors.status.success.light,
  declined: colors.status.error.light,
  tentative: colors.status.warning.light,
  needs_action: colors.text.secondary,
};

function AttendeeRow({ attendee }: Readonly<{ attendee: CalendarEventAttendee }>) {
  return (
    <HStack
      testID={`event_preview_attendee_${attendee.id}`}
      spacing="xs"
      align="center"
      justify="between"
    >
      <VStack spacing="none" style={{ flex: 1, minWidth: 0 }}>
        <Text variant="caption" color={colors.text.primary} numberOfLines={1}>
          {attendee.email}
        </Text>
        {attendee.isOrganizer && (
          <Text variant="caption" color={colors.text.tertiary}>
            Organizer
          </Text>
        )}
      </VStack>
      <Text variant="caption" color={RESPONSE_COLORS[attendee.response]}>
        {RESPONSE_LABELS[attendee.response]}
      </Text>
    </HStack>
  );
}

interface EventPreviewProps {
  event: CalendarEvent;
}

function formatTimeRange(startsAt: string, endsAt: string): string {
  return `${format(new Date(startsAt), 'h:mm a')} – ${format(new Date(endsAt), 'h:mm a')}`;
}

// Mirrors backend-nest/src/calendar/lead-invite-predicate.ts's
// LeadInviteSuppressedReason — a rep reads this, never the raw enum value.
export const LEAD_INVITE_SUPPRESSED_REASON_TEXT: Record<
  CalendarLeadInviteSuppressedReason,
  string
> = {
  org_opt_out: 'this organization has not enabled lead invitations.',
  missing_lead_email: 'the lead has no email on file.',
  do_not_call: 'this lead has opted out of contact (do-not-call).',
  requires_review: 'this lead requires manual review before being contacted.',
  lead_unresolvable: 'the lead record could not be found.',
};

const FALLBACK_SUPPRESSED_REASON_TEXT = 'the reason could not be determined.';

// Silence is the failure mode this exists to prevent: a rep who sees nothing
// here has no way to know an invite was withheld and will assume it was
// sent. Every blocked_consent event renders a reason — falling back to a
// generic one rather than rendering nothing if the reason is ever missing.
function suppressedReasonText(event: CalendarEvent): string | null {
  if (event.leadInviteStatus !== 'blocked_consent') return null;
  return event.inviteSuppressedReason
    ? LEAD_INVITE_SUPPRESSED_REASON_TEXT[event.inviteSuppressedReason]
    : FALLBACK_SUPPRESSED_REASON_TEXT;
}

// ONE shared component, zero trigger logic — how it opens (hover on web,
// long-press on native) lives entirely in useEventPreview and the
// EventPreviewHost.web/.native pair. This file never reads Platform.
export function EventPreview({ event }: Readonly<EventPreviewProps>) {
  const reasonText = suppressedReasonText(event);
  const attendees = event.attendees ?? [];
  const [tab, setTab] = useState<'details' | 'people'>('details');

  // No tab bar for an event with no guests: a People tab that is always empty
  // costs a row of chrome in a 280px popover and answers nothing.
  const showPeople = attendees.length > 0;
  const activeTab = showPeople ? tab : 'details';

  return (
    <Box
      testID={`event_preview_${event.id}`}
      padding="md"
      style={[cardSurface(), { minWidth: 220, maxWidth: 280 }]}
    >
      <VStack spacing="xs">
        <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '600' }}>
          {event.title}
        </Text>

        {showPeople && (
          <Tabs
            testID="event_preview_tabs"
            tabs={[
              { key: 'details', label: 'Details' },
              { key: 'people', label: `People (${attendees.length})` },
            ]}
            activeTab={activeTab}
            onChange={(key) => setTab(key as 'details' | 'people')}
            variant="segment"
          />
        )}

        {activeTab === 'people' ? (
          <VStack spacing="xs">
            <EventAttendeeStack attendees={attendees} />
            {attendees.map((attendee) => (
              <AttendeeRow key={attendee.id} attendee={attendee} />
            ))}
          </VStack>
        ) : (
          <DetailsBody event={event} reasonText={reasonText} />
        )}
      </VStack>
    </Box>
  );
}

function DetailsBody({
  event,
  reasonText,
}: Readonly<{ event: CalendarEvent; reasonText: string | null }>) {
  return (
    <VStack spacing="xs">
      {/* The preview is the one surface with room for the full label, and the
            surface a rep reaches deliberately to ask "what is this?" — so the
            source is spelled out here rather than shown icon-only. This card
            carries no accessibilityLabel of its own, so the indicator's own
            label reaches a screen reader intact. */}
      <EventSourceIndicator
        source={event.source}
        size="md"
        testID={`event_preview_source_${event.id}`}
      />
      <Text
        variant="caption"
        color={colors.text.tertiary}
        style={{ fontVariant: ['tabular-nums'] }}
      >
        {formatTimeRange(event.startsAt, event.endsAt)}
      </Text>
      {event.location && (
        <Text variant="caption" color={colors.text.secondary}>
          {event.location}
        </Text>
      )}
      {event.leadEmail && (
        <Text
          variant="caption"
          color={colors.text.secondary}
          style={{ marginTop: tokens.spacing.xxs }}
        >
          {event.leadEmail}
        </Text>
      )}
      <OpenLeadLink leadId={event.leadId} testID={`event_preview_open_lead_${event.id}`} />
      {reasonText && (
        <Text
          testID="event_preview_invite_suppressed"
          variant="caption"
          color={colors.status.warning.main}
          accessibilityLabel={`Lead not invited: ${reasonText}`}
          style={{ marginTop: tokens.spacing.xxs }}
        >
          Lead not invited — {reasonText}
        </Text>
      )}
      {/* conferenceUrl has been on the row since the model was written and
            was rendered nowhere; this is the one-tap path to the meeting. */}
      <JoinCallButton conferenceUrl={event.conferenceUrl} eventId={event.id} />
    </VStack>
  );
}
